/**
 * SWR (Stale-While-Revalidate) Fetch Utility
 * 
 * Optimized for React SPA "instant feel" navigation:
 * - Returns cached data immediately if within SWR window
 * - Sends If-None-Match and If-Modified-Since headers
 * - Handles 304 Not Modified responses
 * - Background revalidation keeps data fresh
 * 
 * Usage:
 *   const { data, fromCache } = await fetchWithSWR<MyType>('/api/endpoint');
 */

import { isPublicRoute as _isPublicRoute } from '../core/auth/publicRoutes';

const SWR_WINDOW_MS = 10_000; // 10 seconds - return cached immediately, revalidate in background
const SWR_MAX_ENTRIES = 500; // Bound memory: enough for hot navigation, prevents unbounded growth.

interface CacheEntry {
  etag?: string;
  lastModified?: string;
  value: unknown;
  fetchedAt: number;
}

export type FetchWithSWRError = Error & { status?: number; detail?: unknown };
export type FetchWithSWRResult<T> = { data: T; fromCache: boolean; httpStatus?: number };
export type FetchWithSWRConfig = {
  /**
   * Optional cache partition key (e.g. auth/session fingerprint).
   * If omitted, a best-effort partition is derived from CSRF cookie.
   */
  partitionKey?: string;
  /** Override maximum cache entries (global Map) */
  maxEntries?: number;
};

type SWRStats = {
  hits: number;
  misses: number;
  http200: number;
  http304: number;
  evictions: number;
  backgroundOk: number;
  backgroundErrors: number;
};

// Global in-memory cache (per request lifecycle)
const globalAny = globalThis as unknown as { __swrCache?: Map<string, CacheEntry>; __swrStats?: SWRStats };
if (!globalAny.__swrCache) {
  globalAny.__swrCache = new Map<string, CacheEntry>();
}
const swrCache = globalAny.__swrCache;
if (!globalAny.__swrStats) {
  globalAny.__swrStats = {
    hits: 0,
    misses: 0,
    http200: 0,
    http304: 0,
    evictions: 0,
    backgroundOk: 0,
    backgroundErrors: 0,
  };
}
const swrStats = globalAny.__swrStats;

export function getSWRStats(): Readonly<SWRStats> {
  return { ...swrStats };
}

function toHeaderRecord(headers?: HeadersInit): Record<string, string> {
  if (!headers) return {};
  if (headers instanceof Headers) {
    const out: Record<string, string> = {};
    headers.forEach((value, key) => {
      out[key] = value;
    });
    return out;
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  return { ...(headers as Record<string, string>) };
}

function getCookieValue(name: string): string {
  if (typeof document === 'undefined') return '';
  return (document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]+)`))?.[1]) || '';
}

function fnv1a32(text: string): string {
  // Non-cryptographic; goal is to avoid raw sensitive values in cache keys/logs.
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // unsigned 32-bit hex
  return (h >>> 0).toString(16).padStart(8, '0');
}

function getDefaultPartitionKey(): string {
  // Best-effort: CSRF token typically rotates across auth/user changes.
  const csrf = getCookieValue('_eid_csrf_v1');
  if (csrf) return `csrf:${fnv1a32(csrf)}`;
  return 'anon';
}

function touch(key: string): void {
  const v = swrCache.get(key);
  if (!v) return;
  // Map preserves insertion order; delete+set implements simple LRU.
  swrCache.delete(key);
  swrCache.set(key, v);
}

function enforceMaxEntries(maxEntries: number): void {
  while (swrCache.size > maxEntries) {
    const oldestKey = swrCache.keys().next().value as string | undefined;
    if (!oldestKey) return;
    swrCache.delete(oldestKey);
    swrStats.evictions++;
  }
}

/**
 * Generate cache key from request details
 */
function generateCacheKey(url: string, options: RequestInit = {}): string {
  const method = (options.method || 'GET').toUpperCase();
  const bodyStr = options.body ? String(options.body) : '';
  const headers = toHeaderRecord(options.headers);
  // Include a small, safe subset of headers that can legitimately vary responses.
  const vary = [
    `x-plugin-id=${headers['X-Plugin-Id'] ?? headers['x-plugin-id'] ?? ''}`,
    `accept=${headers['Accept'] ?? headers['accept'] ?? ''}`,
    `accept-language=${headers['Accept-Language'] ?? headers['accept-language'] ?? ''}`,
  ].join('&');
  return `${method}:${url}:${bodyStr}:${vary}`;
}

/**
 * Background revalidation (non-blocking)
 */
async function revalidateInBackground(
  url: string,
  cached: CacheEntry,
  options: RequestInit,
  cacheKey: string,
  maxEntries: number,
): Promise<void> {
  try {
    const headers: Record<string, string> = { ...toHeaderRecord(options.headers) };
    if (cached.etag) {
      headers['If-None-Match'] = cached.etag;
    }
    if (cached.lastModified) {
      headers['If-Modified-Since'] = cached.lastModified;
    }
    
    const res = await fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
    
    // 304 Not Modified - still fresh, extend cache
    if (res.status === 304) {
      swrCache.set(cacheKey, { ...cached, fetchedAt: Date.now() });
      enforceMaxEntries(maxEntries);
      swrStats.http304++;
      swrStats.backgroundOk++;
      return;
    }
    
    // Content changed - update cache
    if (res.ok) {
      const data = await res.json();
      swrCache.set(cacheKey, {
        etag: res.headers.get('ETag') || undefined,
        lastModified: res.headers.get('Last-Modified') || undefined,
        value: data,
        fetchedAt: Date.now(),
      });
      enforceMaxEntries(maxEntries);
      swrStats.http200++;
      swrStats.backgroundOk++;
    }
  } catch (error) {
    // Silently fail - cached value still valid
    // Background revalidation should never block UI
    swrStats.backgroundErrors++;
  }
}

/**
 * Fetch with SWR caching and conditional validators
 * 
 * Returns cached value immediately if within SWR window, then revalidates in background.
 * Sends If-None-Match and If-Modified-Since headers for 304 responses.
 * 
 * @param url - API endpoint URL
 * @param options - Fetch options (method, body, headers, etc.)
 * @param swrWindowMs - SWR window in milliseconds (default 10s)
 * @returns Promise with data and fromCache flag
 */
export async function fetchWithSWR<T>(
  url: string,
  options: RequestInit = {},
  swrWindowMs: number = SWR_WINDOW_MS,
  config: FetchWithSWRConfig = {},
): Promise<FetchWithSWRResult<T>> {
  const maxEntries = typeof config.maxEntries === 'number' ? config.maxEntries : SWR_MAX_ENTRIES;
  const partition = (config.partitionKey && config.partitionKey.trim().length > 0)
    ? config.partitionKey
    : getDefaultPartitionKey();

  const cacheKey = `${partition}:${generateCacheKey(url, options)}`;
  const cached = swrCache.get(cacheKey);
  
  // SWR: Return cached immediately if fresh, revalidate in background
  if (cached && Date.now() - cached.fetchedAt < swrWindowMs) {
    swrStats.hits++;
    touch(cacheKey);
    // Background revalidation (fire-and-forget)
    void revalidateInBackground(url, cached, options, cacheKey, maxEntries);
    return { data: cached.value as T, fromCache: true };
  }
  swrStats.misses++;
  
  // Build conditional headers from cache
  const headers: Record<string, string> = { ...toHeaderRecord(options.headers) };
  if (cached?.etag) {
    headers['If-None-Match'] = cached.etag;
  }
  if (cached?.lastModified) {
    headers['If-Modified-Since'] = cached.lastModified;
  }
  
  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });
  
  // 304 Not Modified - reuse cached value
  if (res.status === 304 && cached) {
    // Update fetchedAt to extend SWR window
    swrCache.set(cacheKey, { ...cached, fetchedAt: Date.now() });
    enforceMaxEntries(maxEntries);
    swrStats.http304++;
    return { data: cached.value as T, fromCache: true, httpStatus: 304 };
  }
  
  // 304 without SWR cached data — the BFF or browser HTTP cache sent conditional
  // headers we didn't control. Retry with cache: 'reload' to bypass the browser
  // HTTP cache and get a full 200 response.
  if (res.status === 304 && !cached) {
    swrStats.http304++;
    const retryRes = await fetch(url, {
      ...options,
      headers: toHeaderRecord(options.headers),
      credentials: 'include',
      cache: 'reload',
    });
    if (retryRes.ok) {
      const data = await retryRes.json();
      swrCache.set(cacheKey, {
        etag: retryRes.headers.get('ETag') || undefined,
        lastModified: retryRes.headers.get('Last-Modified') || undefined,
        value: data,
        fetchedAt: Date.now(),
      });
      enforceMaxEntries(maxEntries);
      swrStats.http200++;
      return { data, fromCache: false, httpStatus: 200 };
    }
    // Retry also failed — fall through to normal error handling below
  }
  
  if (res.status === 401) {
    if (!_isPublicRoute()) {
      const interceptor = (globalThis as any).__authInterceptor;
      if (interceptor?.handleUnauthorized) {
        await interceptor.handleUnauthorized(res);
      } else {
        const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/auth/login?return_to=${returnTo}`;
      }
    }
    const err: FetchWithSWRError = new Error('Authentication required');
    err.status = 401;
    throw err;
  }

  if (!res.ok) {
    let detail: unknown = undefined;
    try {
      detail = await res.json();
    } catch {
      // ignore
    }
    const message =
      (detail && typeof detail === 'object' && (detail as any).detail && typeof (detail as any).detail === 'string')
        ? (detail as any).detail
        : `HTTP ${res.status}: ${res.statusText}`;
    const err: FetchWithSWRError = new Error(message);
    err.status = res.status;
    err.detail = detail;
    throw err;
  }
  
  const data = await res.json();
  
  // Update cache with new validators
  swrCache.set(cacheKey, {
    etag: res.headers.get('ETag') || undefined,
    lastModified: res.headers.get('Last-Modified') || undefined,
    value: data,
    fetchedAt: Date.now(),
  });
  enforceMaxEntries(maxEntries);
  swrStats.http200++;
  
  return { data, fromCache: false, httpStatus: 200 };
}

/**
 * Clear SWR cache (useful for logout, data invalidation)
 */
export function clearSWRCache(url?: string): void {
  if (url) {
    // Clear specific URL pattern
    const keysToDelete: string[] = [];
    for (const key of swrCache.keys()) {
      if (key.includes(url)) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => swrCache.delete(key));
  } else {
    // Clear all cache
    swrCache.clear();
  }
}

/**
 * Expose SWR diagnostics on globalThis for dev-console inspection.
 *
 * Usage from browser console:
 *   __swrDiag()          – snapshot of hits, misses, 304s, cache size
 *   __swrDiag.stats()    – raw SWRStats object
 *   __swrDiag.entries()  – list of cached URLs with age in seconds
 *   __swrDiag.clear()    – flush the entire SWR cache
 */
if (typeof globalThis !== 'undefined') {
  const diag = Object.assign(
    function swrDiag() {
      const s = getSWRStats();
      const total = s.hits + s.misses;
      const hitRate = total > 0 ? ((s.hits / total) * 100).toFixed(1) : '0.0';
      return {
        hitRate: `${hitRate}%`,
        hits: s.hits,
        misses: s.misses,
        http200: s.http200,
        http304: s.http304,
        evictions: s.evictions,
        backgroundOk: s.backgroundOk,
        backgroundErrors: s.backgroundErrors,
        cacheSize: swrCache.size,
      };
    },
    {
      stats: getSWRStats,
      entries: () => {
        const now = Date.now();
        const out: Array<{ key: string; ageSeconds: number }> = [];
        for (const [key, entry] of swrCache.entries()) {
          out.push({ key, ageSeconds: Math.round((now - entry.fetchedAt) / 1000) });
        }
        return out;
      },
      clear: () => { clearSWRCache(); return 'SWR cache cleared'; },
    },
  );
  (globalThis as any).__swrDiag = diag;
}
